"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { useTheme } from "next-themes"
import { Toaster as Sonner, ToasterProps } from "sonner"
import {
  CircleAlert,
  CircleCheck,
  Info,
  Loader2,
  TriangleAlert,
} from "lucide-react"
import { cn } from "@/lib/utils"

// Below this measured width a header dock is too narrow to be a usable toast
// column (e.g. a long email leaves little room on a mid-size screen).
const MIN_DOCK_WIDTH = 180
const MAX_DOCK_WIDTH = "28rem"

interface DockRect {
  left: number
  width: number
}

// Tracks the header's [data-toast-dock] element (components/layout/header.tsx)
// so the toaster can be positioned from real layout, not fixed breakpoints.
// The dock only exists once the header renders past the auth/tenant loading
// shell (and never on shell-less routes like /auth), so a MutationObserver on
// <body> re-resolves the element whenever it mounts or unmounts; a
// ResizeObserver on the element itself then tracks width/position cheaply
// while it's around. usePathname() also re-runs the sync on navigation, with
// a rAF retry to catch layout that settles a frame after route change.
function useHeaderDock(): DockRect | null {
  const pathname = usePathname()
  const [rect, setRect] = useState<DockRect | null>(null)

  useEffect(() => {
    let currentEl: HTMLElement | null = null
    let resizeObserver: ResizeObserver | null = null

    const measure = () => {
      if (!currentEl) return
      const domRect = currentEl.getBoundingClientRect()
      const left = Math.round(domRect.left)
      const width = Math.round(domRect.width)
      // Skip the state update (and the Toaster re-render it triggers) when
      // rounded geometry hasn't actually changed — mutation/resize observers
      // fire on every DOM change under <body>, most of which don't move the
      // dock at all.
      setRect((prev) => (prev && prev.left === left && prev.width === width ? prev : { left, width }))
    }

    const sync = () => {
      const el = document.querySelector<HTMLElement>("[data-toast-dock]")
      if (el !== currentEl) {
        resizeObserver?.disconnect()
        currentEl = el
        if (el) {
          resizeObserver = new ResizeObserver(measure)
          resizeObserver.observe(el)
        } else {
          resizeObserver = null
          setRect(null)
        }
      }
      measure()
    }

    const bodyObserver = new MutationObserver(sync)
    bodyObserver.observe(document.body, { childList: true, subtree: true })

    sync()
    window.addEventListener("resize", measure)
    const raf = requestAnimationFrame(sync)

    return () => {
      bodyObserver.disconnect()
      resizeObserver?.disconnect()
      window.removeEventListener("resize", measure)
      cancelAnimationFrame(raf)
    }
  }, [pathname])

  return rect
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const dock = useHeaderDock()
  const docked = dock !== null && dock.width >= MIN_DOCK_WIDTH

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position={docked ? "top-left" : "top-center"}
      offset={docked ? 0 : undefined}
      mobileOffset={docked ? 0 : undefined}
      // Inline styles win over sonner's attribute-selector CSS (including its
      // mobile media query), so the header dock's real geometry always wins
      // without needing `!` classes; falls back to sonner's default
      // top-center offsets when there's no dock to measure.
      style={
        docked && dock
          ? {
              top: 12,
              left: dock.left,
              right: "auto",
              transform: "none",
              width: `min(${dock.width}px, ${MAX_DOCK_WIDTH})`,
            }
          : undefined
      }
      // Collapsed stack: older toasts peek 6px below the newest, which keeps
      // the peek inside the header bar; hovering expands the stack downward.
      gap={6}
      visibleToasts={3}
      icons={{
        success: <CircleCheck className="size-4" />,
        error: <CircleAlert className="size-4" />,
        warning: <TriangleAlert className="size-4" />,
        info: <Info className="size-4" />,
        loading: <Loader2 className="size-4 animate-spin" />,
      }}
      toastOptions={{
        // Sonner defaults to 4s, which is too quick to read anything but the
        // shortest confirmation — and many call sites show a server message
        // (`apiErrorMessage` in lib/api/errors.ts), so the text length
        // isn't ours to predict. Hovering pauses the timer *and* expands the
        // clamped pill, so this only governs how long an unattended toast sits
        // in the header dock; nothing here blocks the page.
        duration: 8000,
        unstyled: true,
        classNames: {
          toast: cn(
            "flex w-full min-h-10 items-center gap-2.5 rounded-xl border bg-background px-3.5 py-1.5",
            "text-sm text-foreground shadow-[0_2px_8px_-2px_rgb(0_0_0/0.08)]",
            // Hide a collapsed toast's content behind the front one.
            "data-[front=false]:data-[expanded=false]:*:opacity-0",
            // While docked at ≥md, clamp a collapsed toast's title/description
            // to keep the pill compact; the full stack expansion (hover or
            // keyboard focus sets data-expanded="true") reveals full text.
            // Never clamp on narrow/non-docked layouts (auth pages, mobile).
            docked &&
              cn(
                "md:[&_[data-title]]:line-clamp-2 md:[&_[data-description]]:line-clamp-1",
                "md:data-[expanded=true]:[&_[data-title]]:line-clamp-none",
                "md:data-[expanded=true]:[&_[data-description]]:line-clamp-none"
              )
          ),
          icon: "relative flex size-4 shrink-0 items-center text-muted-foreground",
          content: "flex min-w-0 flex-1 flex-col",
          title: "leading-5 font-medium",
          description: "text-xs leading-4 text-muted-foreground",
          actionButton:
            "shrink-0 rounded-md px-2 py-1 text-xs font-medium text-foreground hover:bg-muted",
          cancelButton:
            "shrink-0 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted",
          success: "[&_[data-icon]]:text-emerald-600 dark:[&_[data-icon]]:text-emerald-400",
          error: "[&_[data-icon]]:text-destructive",
          warning: "[&_[data-icon]]:text-amber-500",
          info: "[&_[data-icon]]:text-sky-600 dark:[&_[data-icon]]:text-sky-400",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
